import React from 'react';
import { Avatar } from 'antd';
import { CardHeader, Modal, Typography } from 'ui-kit';
import './ProductModal.scss';
import { getCategoryForProductCard } from '../utils';
import { ProductCategory, ProductExtraField, ProductExtraFieldValue } from 'api/types/product';
import placeholder from 'components/Dashboard/Store/Products/images/placeholder.png';
import ProductCartLabel from '../ProductCartLabel';
import dayjs from 'dayjs';
import ProductStoreForm, { InitialValues } from './ProductStoreForm';
import { UrlText } from 'components/UrlText';
import { EPriceType } from 'components/NewProductStepper/ProductBaseForm/constants';
import { IProductPrice } from 'api/product_sdk';

interface IProductModal {
  title: string;
  description?: string;
  cancellationPolicy?: string;
  amount: number;
  price: string;
  currency: string;
  imgSrc?: string;
  onAdd: (params: {
    quantity: number;
    deliveryDate: string;
    note: string;
    extraFields: ProductExtraFieldValue[];
    price: number;
    priceType: string;
    productPriceId?: number | undefined;
  }) => void;
  quantity: number;
  category?: ProductCategory;
  hasQuantity: boolean;
  subcategory?: string;
  onCancel: () => void;
  visible: boolean;
  isPreorder?: boolean;
  isRequest?: boolean;
  isLocal?: boolean;
  isLink?: boolean;
  externalLink?: string;
  affiliateLink?: string;
  storeId?: number;
  preorderBy?: number | null;
  deliveryDate: string;
  note: string;
  extraFields?: ProductExtraField[];
  extraFieldValues?: ProductExtraFieldValue[];
  priceType: EPriceType;
  productPrices: IProductPrice[];
  isEarlyCheckin?: boolean;
  isLateCheckout?: boolean;
  selectedProductPrice?: IProductPrice;
  showDeliveryDate: boolean;
}

const ProductModal = ({
  title,
  description,
  cancellationPolicy: cancellation_policy,
  amount,
  currency,
  price,
  onAdd,
  onCancel,
  quantity,
  imgSrc,
  category,
  subcategory,
  isPreorder,
  isRequest,
  isLocal,
  isLink,
  externalLink,
  affiliateLink,
  storeId,
  visible,
  preorderBy,
  deliveryDate,
  note,
  hasQuantity,
  extraFields,
  extraFieldValues,
  priceType,
  productPrices,
  isEarlyCheckin,
  isLateCheckout,
  selectedProductPrice,
  showDeliveryDate,
}: IProductModal) => {
  const onSubmit = ({
    date,
    amount,
    note,
    extraFields,
    guestPrice,
    priceType,
    selectedProductPriceId,
  }: InitialValues) => {
    if (isLink) {
      if (isLocal) {
        const url = new URL(affiliateLink);
        url.searchParams.append('store', storeId.toString());
        window.open(url, '_blank');
      } else {
        const url = new URL(externalLink);
        window.open(url, '_blank');
      }
      return;
    }

    const selectedProductPrice = productPrices?.find(
      (price) => price.id === selectedProductPriceId,
    );

    const payload = {
      quantity: amount,
      deliveryDate: date && dayjs(date).format('YYYY-MM-DD'),
      note,
      extraFields,
      price:
        selectedProductPrice && !selectedProductPrice.isVariablePrice
          ? selectedProductPrice.price
          : Number(guestPrice),
      priceType,
      productPriceId: selectedProductPriceId,
    };

    onAdd(payload);
  };

  return (
    <Modal
      footer={null}
      className="ProductModal"
      visible={visible}
      destroyOnClose
      onCancel={onCancel}
    >
      <div className="ProductModal-container">
        <div className="ProductModal-container-media">
          {isPreorder && !isRequest && <ProductCartLabel label="Pre-order" />}
          {isRequest && <ProductCartLabel label="Request" />}
          {isLink && <ProductCartLabel label="Link" />}
          <Avatar className="ProductModal-container-media__image" src={imgSrc || placeholder} />
        </div>
        <div className="ProductModal-container-content">
          <CardHeader
            title={title}
            subheader={getCategoryForProductCard(category, subcategory, description, true)}
          />
          <Typography variant="body2" className="ProductModal-container-content__description">
            <UrlText text={description} />
          </Typography>
          {cancellation_policy ? (
            <div className="ProductModal-container-content__cancellation">
              <Typography
                variant="subtitle2"
                className="ProductModal-container-content__cancellation-title"
                style={{ fontWeight: 600 }}
              >
                Cancellation policy
              </Typography>

              <Typography
                variant="body2"
                className="ProductModal-container-content__cancellation-text"
                style={{ fontWeight: 600 }}
              >
                <UrlText text={cancellation_policy} />
              </Typography>
            </div>
          ) : null}
          <ProductStoreForm
            showDeliveryDate={showDeliveryDate}
            isPreorder={isPreorder}
            isLocal={isLocal}
            isLink={isLink}
            currency={currency}
            hasQuantity={hasQuantity}
            quantity={quantity}
            price={price}
            onSubmit={onSubmit}
            extraFields={extraFields}
            initialValues={{
              amount,
              note,
              preorderBy,
              extraFields: extraFieldValues,
              date: preorderBy >= 0 && !deliveryDate ? undefined : deliveryDate,
              guestPrice: Number(price),
              priceType,
              selectedProductPriceId: selectedProductPrice?.id,
            }}
            priceType={priceType}
            productPrices={productPrices}
            isEarlyCheckin={isEarlyCheckin}
            isLateCheckout={isLateCheckout}
          />
        </div>
      </div>
    </Modal>
  );
};

export default ProductModal;
